import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { confirmLogoutWithUnsent } from '../utils/logoutGuard';
import { useSubscription } from '../SubscriptionContext';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiCheckCircle, FiLogOut } from 'react-icons/fi';
import AppLoading from './AppLoading';
import { APP_FEATURES } from '../appFeatures';
import './Paywall.css';

/* The single subscription gate. Shows when a native-app user is not yet
   entitled. Web users never see this (no IAP in browsers). */
export default function Paywall() {
  const { proPackage, purchase, restore, error, isReady, welcome, finishWelcome } = useSubscription();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // 'buy' | 'restore' | null
  const [localError, setLocalError] = useState(null);

  const product       = proPackage?.product;
  const priceLabel    = product?.priceString || '£5.99';
  const periodLabel   = 'per year';
  const trialDays     = freeTrialInfo(product); // free-trial length in days, or null
  const hasTrial      = trialDays != null;

  // "You're in", held long enough to read, then faded off the app that has
  // been mounting underneath it (SubscriptionGate). WELCOME_MS + the CSS fade.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (!welcome) return undefined;
    const fade = setTimeout(() => setLeaving(true), WELCOME_MS);
    const done = setTimeout(finishWelcome, WELCOME_MS + FADE_MS);
    return () => { clearTimeout(fade); clearTimeout(done); };
  }, [welcome, finishWelcome]);

  // Still fetching offerings from RevenueCat — don't render a broken paywall.
  if (!isReady) return <AppLoading />;

  if (welcome) {
    return (
      <div className={`paywall-page paywall-welcome${leaving ? ' is-leaving' : ''}`}
           role="status" aria-live="polite">
        <div className="paywall-bg" aria-hidden="true" />
        <div className="paywall-welcome-inner">
          <div className="paywall-welcome-tick"><FiCheck /></div>
          <h1 className="paywall-title">You’re in</h1>
          <p className="paywall-sub">Welcome to 1-19. Everything is unlocked.</p>
        </div>
      </div>
    );
  }

  const handlePurchase = async () => {
    setBusy('buy');
    setLocalError(null);
    try {
      // purchase() already holds the customer info the store returned; a second
      // round trip here only delayed the hand-off and redrew Home twice.
      await purchase(proPackage);
    } catch (e) {
      // RC throws { userCancelled: true } on user-initiated cancel
      if (!e?.userCancelled && e?.code !== '1' /* PURCHASE_CANCELLED */) {
        setLocalError(e?.message || 'Purchase could not be completed. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  const handleRestore = async () => {
    setBusy('restore');
    setLocalError(null);
    try {
      const result = await restore();
      // A successful restore that finds nothing doesn't throw — detect it so
      // we don't leave the user staring at the paywall with no feedback.
      const ci     = result?.customerInfo ?? result;
      const active = ci?.entitlements?.active || {};
      const subs   = ci?.activeSubscriptions || [];
      if (Object.keys(active).length === 0 && subs.length === 0) {
        setLocalError('No previous purchase found to restore on this Apple ID.');
      }
      // If something WAS restored, the gate re-renders and dismisses the paywall.
    } catch (e) {
      setLocalError('Couldn’t restore purchases. Please check your connection and try again.');
    } finally {
      setBusy(null);
    }
  };

  const handleSignOut = async () => {
    if (!(await confirmLogoutWithUnsent())) return;
    try { await logout(); } catch {}
    navigate('/login');
  };

  return (
    <div className="paywall-page">
      <div className="paywall-bg" aria-hidden="true" />

      <div className="paywall-inner">
        <img
          src={`${process.env.PUBLIC_URL}/1_19_logo.png`}
          alt="1-19"
          className="paywall-logo"
        />

        <h1 className="paywall-title">Unlock the full 1-19 experience</h1>
        <p className="paywall-sub">
          Tournaments, live leaderboards, insights &amp; offline scoring —
          everything we build, always included.
        </p>

        <ul className="paywall-features">
          {APP_FEATURES.map((f) => (
            <li key={f}><FiCheckCircle /> {f}</li>
          ))}
        </ul>

        <div className="paywall-price-card">
          <div className="paywall-founding-badge">★ Founding Member</div>
          <div className="paywall-price">{priceLabel} <span>{periodLabel}</span></div>
        </div>

        <p className="paywall-founding-note">
          Every feature we build, included — and you help shape what comes next.
        </p>

        {(localError || error || !proPackage) && (
          <div className="paywall-error">
            {localError || error || 'Subscription products could not be loaded. Please check your connection and try again.'}
          </div>
        )}

        <button
          className="paywall-cta"
          onClick={handlePurchase}
          disabled={!proPackage || busy === 'buy'}
          style={!proPackage ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          {busy === 'buy' ? (
            <span className="paywall-cta-main">{storeWaitLabel()}</span>
          ) : hasTrial ? (
            <>
              <span className="paywall-cta-main">Subscribe · {priceLabel}/year</span>
              <span className="paywall-cta-sub">{trialDays}-day free trial included · auto-renews</span>
            </>
          ) : (
            <span className="paywall-cta-main">Subscribe · {priceLabel}/year</span>
          )}
        </button>

        <button
          className="paywall-secondary"
          onClick={handleRestore}
          disabled={busy === 'restore'}
        >
          {busy === 'restore' ? 'Checking…' : 'Restore Purchases'}
        </button>

        <p className="paywall-legal">
          A subscription will be charged to your Apple ID or Google account at
          confirmation of purchase. It renews automatically unless cancelled at
          least 24 hours before the renewal date. You can manage and cancel any
          time in your store account settings.
        </p>

        <div className="paywall-links">
          <button type="button" onClick={() => navigate('/legal?tab=terms')}>
            Terms of Use
          </button>
          <span>·</span>
          <button type="button" onClick={() => navigate('/legal?tab=privacy')}>
            Privacy Policy
          </button>
        </div>

        <button className="paywall-signout" onClick={handleSignOut}>
          <FiLogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

const WELCOME_MS = 1300;
const FADE_MS = 320; // matches .paywall-welcome.is-leaving in Paywall.css

/* The button's label while the store's own payment sheet is open — which can
   be a while (Face ID, a password, a card check), and "Starting…" for all of
   it read as the app being stuck. */
function storeWaitLabel() {
  return Capacitor.getPlatform() === 'android'
    ? 'Waiting for Google Play…'
    : 'Waiting for the App Store…';
}

/* Returns the real free-trial length in days from RevenueCat product metadata,
   or null when the product has no free introductory offer. Trial messaging is
   shown ONLY when this is non-null, so we never advertise a trial the store
   won't actually grant (App Store guideline 3.1.2). */
function freeTrialInfo(product) {
  const intro = product?.introPrice;
  if (!intro) return null;

  // The intro offer must be free (a £0 / "Free" introductory period).
  const isFree = intro.price === 0
    || /^(free|£?\s*0|\$?\s*0)/i.test((intro.priceString || '').trim());
  if (!isFree) return null;

  // RC exposes the period as a unit + count (e.g. WEEK × 1, DAY × 7).
  const n    = Number(intro.periodNumberOfUnits) || 1;
  const unit = String(intro.periodUnit || intro.period || '').toUpperCase();

  let days = null;
  if (/W/.test(unit))      days = n * 7;
  else if (/D/.test(unit)) days = n;
  else if (/M/.test(unit)) days = n * 30;
  else if (/Y/.test(unit)) days = n * 365;

  return days || 7;
}
